'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cx } from './cx';

export type KToastTone = 'ok' | 'warn' | 'error';

/**
 * КАПСУЛА-УВЕДОМЛЕНИЕ КАБИНЕТА — одна на кабинет (PAY-FLOW-TZ §16 К12, §17.2 п.2; рецепт перенесён из
 * `.scn-run-toast`): стеклянная капсула внизу поверх контента над safe-area, по центру, 13/600; поток страницы
 * не трогает (`position: fixed`), появление и уход — прозрачность с подъёмом (`.k-toast` в kit.css).
 * `tone`: `ok` — зелёная роль успеха, `warn` — янтарная, `error` — капсула нейтральная, красный (`--red`)
 * только у значка. `onDone` задан — капсула уходит сама через 3 с (доиграв уход) и зовёт его; не задан —
 * временем жизни правит хозяин (снимает капсулу с разметки сам).
 */
export function KToast({
  text,
  tone = 'ok',
  onDone,
  className,
}: {
  text: ReactNode;
  tone?: KToastTone;
  onDone?: () => void;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [leaving, setLeaving] = useState(false);
  const done = useRef(onDone);
  done.current = onDone;
  const timed = !!onDone;
  // Новый текст — новый отсчёт 3 с (повторная отправка, пока капсула видна).
  useEffect(() => {
    setLeaving(false);
    if (!timed) return;
    const id = window.setTimeout(() => setLeaving(true), TOAST_MS);
    return () => window.clearTimeout(id);
  }, [text, timed]);
  // Уход доигрывает кадры CSS (`[data-state='closed']`); кадров нет — снимаем сразу (как useExitPresence).
  useEffect(() => {
    if (!leaving) return;
    const el = ref.current;
    const name = el ? getComputedStyle(el).animationName : '';
    if (!el || !name || name === 'none') {
      done.current?.();
      return;
    }
    const end = (e: AnimationEvent): void => {
      if (e.target === el) done.current?.();
    };
    el.addEventListener('animationend', end);
    el.addEventListener('animationcancel', end);
    return () => {
      el.removeEventListener('animationend', end);
      el.removeEventListener('animationcancel', end);
    };
  }, [leaving]);
  return (
    <div
      ref={ref}
      className={cx('k-toast', tone !== 'ok' && `k-toast--${tone}`, className)}
      role="status"
      data-state={leaving ? 'closed' : 'open'}
    >
      {tone === 'error' ? (
        <svg className="k-toast__ico" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="10" fill="currentColor" />
          <path d="M12 7v6" stroke="var(--surf)" strokeWidth="2.2" strokeLinecap="round" />
          <circle cx="12" cy="16.6" r="1.3" fill="var(--surf)" />
        </svg>
      ) : null}
      {text}
    </div>
  );
}

/** Сколько капсула стоит на экране (§16 К12: «уходит сама через 3 с»). */
export const TOAST_MS = 3000;
